import {css, StyleSheet} from 'aphrodite';
import React from 'react';
import _ from 'underscore';

import UploadSpreadsheetModalPane from 'bulk-import/panes/UploadSpreadsheetModalPane';
import * as SpreadsheetBulkImportActions from 'bulk-import/SpreadsheetBulkImportActions';
import {IMPORT_TYPE, Pane, PaneKey} from 'bulk-import/SpreadsheetBulkImportConstants';
import SpreadsheetBulkImportModalContainer from 'bulk-import/SpreadsheetBulkImportModalContainer';
import {colNumToExcelId} from 'bulk-import/SpreadsheetBulkImportUtil';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getAllValidBulkImportColumnTypes} from 'lab-automation/settings/automation-output-file-config/AutomationOutputFileColumnUtils';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {Row, Table, TableHeaderControlsArgs} from 'notebook/tables/TableConstants';
import {AllTagSchemasQuery} from 'registry/graphql/__generated/AllTagSchemasQuery';
import {ALL_TAG_SCHEMAS_QUERY} from 'registry/graphql/AllTagSchemasQuery';
import {AssayRunSchema} from 'results/assay-schemas/AssaySchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {ButtonStyle} from 'ui-library/buttons/Button';
import runModal from 'ui-library/runModal';
import {capwords} from 'util/NameUtil';
import {withLoadingScreen} from 'util/Promises';

import {TagSchemaListingWithFieldsGraphQL} from '../../../../graphql-fragments/schemas/__generated/TagSchemaListingWithFieldsGraphQL';
import {
  automationFileConfigStyles,
  AutomationOutputFileConfigEditorProps,
} from '../AutomationFileConfigUtils';
import {IconButton} from '../RunSchemaEditorActionButton';
import {
  AutomationOutputFileConfigSpec,
  ClientAutomationOutputFileConfigSpec,
  getColumnTypeDisplayName,
} from '../RunSchemaSpecConstants';

// Set to fit all column
const DEFAULT_COLUMN_WIDTH = 180;

// exported for test
export const DELETE_DATA_TEST_ATTRS = {
  delete: {component: 'SetColumnsMapEditor', element: 'delete'},
};
export const EDIT_DATA_TEST_ATTRS = {
  edit: {component: 'SetColumnsMapEditor', element: 'edit'},
};

function _hasColumns(outputFileConfig: ClientAutomationOutputFileConfigSpec): boolean {
  if (
    (outputFileConfig.columnTypes && outputFileConfig.columnTypes.length) ||
    (outputFileConfig.columnTypesByName && Object.keys(outputFileConfig.columnTypesByName).length)
  ) {
    return true;
  }
  return false;
}

function _getTableName(outputFileConfig: ClientAutomationOutputFileConfigSpec): string {
  let length = 0;
  if (outputFileConfig.columnTypes) {
    length = outputFileConfig.columnTypes.length;
  } else {
    length = Object.keys(outputFileConfig.columnTypesByName).length;
  }
  return `${length} columns mapped`;
}

function _createColumnLabelsForColumnTypes(
  outputFileConfig: ClientAutomationOutputFileConfigSpec
): Array<string> {
  if (outputFileConfig.columnTypes) {
    return outputFileConfig.columnTypes.map((column, index) => colNumToExcelId(index));
  }
  return Object.keys(outputFileConfig.columnTypesByName);
}

function _createTableRowForColumnTypes(outputFileConfig: ClientAutomationOutputFileConfigSpec): Row {
  // Convert CAPITAL_WITH_UNDERSCORES to Capital With Underscores
  if (outputFileConfig.columnTypes) {
    return outputFileConfig.columnTypes.map((column) => ({
      text: capwords(getColumnTypeDisplayName(column)),
    }));
  }
  return Object.keys(outputFileConfig.columnTypesByName).map((column) => ({
    text: capwords(getColumnTypeDisplayName(outputFileConfig.columnTypesByName[column].columnType)),
  }));
}

function _createColumnsMapTable(
  outputFileConfig: ClientAutomationOutputFileConfigSpec,
  columnWidths: Array<number>
): Table {
  return {
    name: _getTableName(outputFileConfig),
    table: [_createTableRowForColumnTypes(outputFileConfig)],
    widths: columnWidths,
    columnConstraints: [],
    headerRow: false,
    headerCol: false,
    columnLabels: _createColumnLabelsForColumnTypes(outputFileConfig),
    lockedDims: true,
    allowColumnEdits: false,
    _hideRename: true,
  };
}

function getPanes(
  args: SetColumnsMapModalArgs,
  allTagSchemasById: Record<string, TagSchemaListingWithFieldsGraphQL>
): Array<Pane> {
  return [
    {
      paneKey: PaneKey.INSTRUMENT_DATA,
      configuration: {
        nextPaneKey: PaneKey.MAP_OUTPUT_FILE_COLUMNS,
        connectionSchemaId: args.baseSchema?.connectionSchema?.id,
        delimiter: args.outputFileConfig.delimiter,
        automationOutputFileConfig: args.outputFileConfig,
        shouldShowUploadedFilesOnly: args.shouldShowUploadedFilesOnly || false,
      },
    },
    {
      paneKey: PaneKey.CONNECTION_FILES,
      configuration: {
        nextPaneKey: PaneKey.MAP_OUTPUT_FILE_COLUMNS,
        connectionSchemaId: args.baseSchema?.connectionSchema?.id,
        delimiter: args.outputFileConfig.delimiter,
        automationOutputFileConfig: args.outputFileConfig,
      },
    },
    {
      paneKey: PaneKey.UPLOAD_SPREADSHEET,
      configuration: UploadSpreadsheetModalPane.configure({hideBackButton: true}),
    },
    {
      paneKey: PaneKey.MAP_OUTPUT_FILE_COLUMNS,
      configuration: {
        initialOutputFileConfig: args.outputFileConfig,
        initialErrors: [],
        handleUpdatedOutputFileConfig: args.onUpdateConfig,
        getValidColumnTypes: () =>
          Array.from(getAllValidBulkImportColumnTypes(args.outputFileConfig, allTagSchemasById)),
        goToImportInstrumentData: SpreadsheetBulkImportActions.goToPaneWithKey.bind(
          null,
          PaneKey.INSTRUMENT_DATA
        ),
        goToImportConnectionFiles: SpreadsheetBulkImportActions.goToPaneWithKey.bind(
          null,
          PaneKey.CONNECTION_FILES
        ),
        goToUploadSpreadsheet: SpreadsheetBulkImportActions.goToPaneWithKey.bind(
          null,
          PaneKey.UPLOAD_SPREADSHEET
        ),
        ownerId: args.ownerId,
      },
    },
  ];
}

function getSelectedSchemaIds(outputFileConfig: AutomationOutputFileConfigSpec): {
  initialContainerSchemaId?: string | null;
  initialResultSchemaId?: string | null;
  initialTagSchemaId?: string | null;
  initialPlateSchemaId?: string | null;
  initialBoxSchemaId?: string | null;
} {
  const destinationInfo = outputFileConfig.destinationInfo;
  return {
    initialContainerSchemaId: destinationInfo?.containerSchema,
    initialPlateSchemaId: destinationInfo?.plateSchema,
    initialBoxSchemaId: destinationInfo?.boxSchema,
    initialTagSchemaId: outputFileConfig.entitySchemaInfo?.schema,
    initialResultSchemaId: outputFileConfig.resultSchemaInfo?.schema,
  };
}

interface SetColumnsMapModalArgs {
  outputFileConfig: ClientAutomationOutputFileConfigSpec;
  baseSchema: AssayRunSchema | null;
  onUpdateConfig: (newConfig: ClientAutomationOutputFileConfigSpec) => void;
  ownerId: string;
  shouldShowUploadedFilesOnly?: boolean;
}

export async function openSetColumnsMapModal(args: SetColumnsMapModalArgs): Promise<void> {
  const data = await withLoadingScreen(doQuerySafe<AllTagSchemasQuery, {}>({query: ALL_TAG_SCHEMAS_QUERY}));
  const allTagSchemasById: Record<string, TagSchemaListingWithFieldsGraphQL> = _.indexBy(
    data.tagSchemas,
    'id'
  );
  runModal(SpreadsheetBulkImportModalContainer, {
    onImportFromSpreadsheet: () => {},
    onRequestClose: SpreadsheetBulkImportActions.closeModal,
    importType: IMPORT_TYPE.EXAMPLE_FILE,
    importItemWord: 'row of output',
    title: 'Set columns',
    panes: getPanes(args, allTagSchemasById),
    initialSettings: {
      initialPaneKey: PaneKey.MAP_OUTPUT_FILE_COLUMNS,
      ...getSelectedSchemaIds(args.outputFileConfig),
    },
  });
}

export default function SetColumnsMapEditor(
  props: AutomationOutputFileConfigEditorProps
): JSX.Element | null {
  if (!_hasColumns(props.outputFileConfig)) {
    return (
      <div className={css(automationFileConfigStyles.sectionDescriptionAbove)}>
        <a
          className={css(automationFileConfigStyles.formatSectionItem)}
          onClick={() => {
            openSetColumnsMapModal(props);
          }}
          {...getTestProps({
            component: 'SetColumnsMapEditor',
            element: 'action',
          })}
        >
          Set columns
        </a>
      </div>
    );
  }
  const defaultColumnWidths: Array<number> = [];
  if (props.outputFileConfig.columnTypes) {
    defaultColumnWidths.push(...props.outputFileConfig.columnTypes.map(() => DEFAULT_COLUMN_WIDTH));
  } else {
    defaultColumnWidths.push(
      ...Object.keys(props.outputFileConfig.columnTypesByName).map(() => DEFAULT_COLUMN_WIDTH)
    );
  }

  const renderHeaderControls = ({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null => {
    if (isCollapsed) {
      return null;
    }
    return (
      <div>
        <IconButton
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          className="u-mini-spacing-right"
          icon="delete"
          onClick={() => {
            if (props.outputFileConfig.columnTypes) {
              props.onUpdateConfig({
                ...props.outputFileConfig,
                columnTypes: [],
                datasetConfigJson: undefined,
              });
            } else {
              props.onUpdateConfig({
                ...props.outputFileConfig,
                columnTypesByName: {},
                datasetConfigJson: undefined,
              });
            }
          }}
          dataTestAttrs={DELETE_DATA_TEST_ATTRS.delete}
        />
        <IconButton
          buttonStyle={ButtonStyle.TERTIARY_GRAY}
          icon="edit"
          onClick={() => {
            openSetColumnsMapModal(props);
          }}
          dataTestAttrs={EDIT_DATA_TEST_ATTRS.edit}
        />
      </div>
    );
  };

  return (
    <div className={`simpleDivTable-row mediocre ${css(styles.columnTableContainer, styles.divTableRow)}`}>
      <SimpleTableEditable
        {...standaloneProps}
        table={_createColumnsMapTable(props.outputFileConfig, defaultColumnWidths)}
        cid="output-config-columns-1"
        hideFormulaBox={true}
        isCompactBottomSpacing={true}
        locked={true}
        headerControls={renderHeaderControls}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  columnTableContainer: {
    display: 'inline-block',
  },
  divTableRow: {
    ':hover': {
      background: 'white',
    },
    borderTop: 'none',
    padding: 0,
  },
});
